import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { wholesaleCart as cartApi } from '../services/api';

const CartContext = createContext(null);
const CART_KEY = 'wholesaleCart';

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || {}; }
    catch { return {}; }
  });

  // Track whether initial server hydration has completed
  const hydratedRef = useRef(false);
  // Debounce timer ref for server sync
  const syncTimerRef = useRef(null);

  // On mount: hydrate from server (if authenticated)
  useEffect(() => {
    const token = localStorage.getItem('wholesaleToken');
    if (!token) return;
    cartApi.get()
      .then(res => {
        const rows = res?.data?.data || [];
        if (rows.length) {
          const serverCart = {};
          rows.forEach(r => { serverCart[r.productID] = r.quantity; });
          setCart(serverCart);
          localStorage.setItem(CART_KEY, JSON.stringify(serverCart));
        }
      })
      .catch(() => { /* non-fatal: keep local cart */ })
      .finally(() => { hydratedRef.current = true; });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync cart to server (debounced 500ms) whenever cart changes after hydration
  useEffect(() => {
    if (!hydratedRef.current) return;
    const token = localStorage.getItem('wholesaleToken');
    if (!token) return;

    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      const items = Object.entries(cart).map(([productID, quantity]) => ({
        productID: parseInt(productID),
        quantity,
      }));
      cartApi.set(items).catch(() => { /* non-fatal — log silently */ console.warn('[Cart] Server sync failed'); });
    }, 500);

    return () => { if (syncTimerRef.current) clearTimeout(syncTimerRef.current); };
  }, [cart]);

  const persist = (newCart) => {
    setCart(newCart);
    localStorage.setItem(CART_KEY, JSON.stringify(newCart));
  };

  const addToCart = useCallback((productId, qty = 1) => {
    setCart(prev => {
      const next = { ...prev, [productId]: (prev[productId] || 0) + qty };
      localStorage.setItem(CART_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const setQty = useCallback((productId, qty) => {
    setCart(prev => {
      let next;
      if (qty <= 0) {
        next = { ...prev };
        delete next[productId];
      } else {
        next = { ...prev, [productId]: qty };
      }
      localStorage.setItem(CART_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart(prev => {
      const next = { ...prev };
      delete next[productId];
      localStorage.setItem(CART_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    setCart({});
    localStorage.removeItem(CART_KEY);
    // Also clear server cart (non-fatal)
    const token = localStorage.getItem('wholesaleToken');
    if (token) {
      cartApi.clear().catch(() => {});
    }
  }, []);

  const cartCount = Object.values(cart).reduce((a, b) => a + b, 0);
  const cartProductIds = Object.keys(cart).map(Number);

  return (
    <CartContext.Provider value={{ cart, addToCart, setQty, removeFromCart, clearCart, cartCount, cartProductIds, persist }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}

export default CartContext;
